<template>
  <div
    class="cb-alternative-login"
    v-documentClick="{
      handler: 'handleDocumentClick',
    }"
    @keydown="handleKeydown"
  >
    <h1 tabindex="-1" ref="title" class="cb-heading-5">
      For Security Reasons, <br />Please Log In to Continue
    </h1>
    <p
      class="cb-alternative-login__global-error cb-body-2"
      v-show="formErrorMessage !== ''"
      aria-live="polite"
    >
      {{ formErrorMessage }}
    </p>
    <p class="cb-alternative-login__error cb-body-2">
      <span v-show="formErrors.telephoneNumber && $v.formData.telephoneNumber.$error">
         {{ formErrors.telephoneNumber }}
        <br />
      </span>

      <span v-show="formErrors.pic && formError" aria-live="polite">
        {{ formErrors.pic }}
        <br />
      </span>
    </p>
    <form class="cb-alternative-login__form-container">
      <div class="cb-alternative-login__text-input">
        <cb-text-input
          :error="$v.formData.telephoneNumber.$error"
          :errorText="formErrors.telephoneNumber"
          @blur="
            handleBlur(
              'telephoneNumber',
              `Oops, you entered an invalid number. Your number must have
              10 digits and can\'t start with a 1 or 0.`,
            )
          "
          @focus="$v.formData.telephoneNumber.$reset()"
          @input="
            setFieldErrorMessage(
              'telephoneNumber',
              `Oops, you entered an invalid number. Your number must have
              10 digits and can\'t start with a 1 or 0.`,
            )
          "
          id="login"
          label="Primary Phone Number"
          required
          :maxLength="14"
          tabOrder="first"
          type="text"
          v-model="formData.telephoneNumber"
          v-mask="'(###) ###-####'"
        ></cb-text-input>
        <button
          :aria-expanded="isTooltipOpen.telephoneNumber ? 'true' : 'false'"
          @click.prevent="handleInfoIconClick('telephoneNumber')"
          type="button"
          aria-label="Primary Phone Number, more info"
          ref="telephone-number-tooltip-btn"
        >
          <img alt src="../../assets/icons/info.svg" />
        </button>
        <div
          class="cb-tooltip"
          ref="telephone-number-tooltip"
          v-if="isTooltipOpen.telephoneNumber"
        >
          <div class="cb-tooltip__arrow"></div>
          <p class="cb-overline">
            This is the phone number you provided to ADT as your primary contact
            number. It's usually the first number ADT will call should an alarm
            occur. You may have provided your home, business, or mobile phone
            number. Your account number is located on the top right corner of
            your bill.
          </p>
          <p class="cb-overline assistance">
            <strong>For assistance call:</strong>
          </p>
          <p class="cb-overline">(800) 724-9097</p>
        </div>
      </div>
      <div class="cb-alternative-login__text-input">
        <cb-text-input
          :error="$v.formData.pic.$error"
          :errorText="formErrors.pic"
          @blur="handleBlur('pic', 'Verbal security password must be entered.')"
          @focus="$v.formData.pic.$reset()"
          @input="
            setFieldErrorMessage(
              'pic',
              'Verbal security password must be entered.',
            )
          "
          id="password"
          label="Verbal Security Password"
          required
          type="password"
          v-model="formData.pic"
        ></cb-text-input>
        <button
          :aria-expanded="isTooltipOpen.pic ? 'true' : 'false'"
          @click.prevent="handleInfoIconClick('pic')"
          type="button"
          aria-label="Verbal Security Password, more info"
          ref="pic-tooltip-btn"
        >
          <img alt src="../../assets/icons/info.svg" />
        </button>
        <div class="cb-tooltip" ref="pic-tooltip" v-if="isTooltipOpen.pic">
          <div class="cb-tooltip__arrow"></div>
          <p class="cb-overline">
            This is the unique word you provide us if we call you during an
            alarm event. It should be different than the code you enter on your
            touchpad to arm and disarm your ADT system.
          </p>
          <p class="cb-overline assistance">
            <strong>For assistance call:</strong>
          </p>
          <p class="cb-overline">(800) 724-9097</p>
        </div>
      </div>
      <cb-button
        class="cb-alternative-login__submit-btn"
        type="submit"
        :loading="loading"
        @click="handleLogin"
        >Log In</cb-button
      >
    </form>
    <div class="cb-alternative-login__divider"></div>
    <cb-assistance tabOrder="last"></cb-assistance>
  </div>
</template>

<script>
/* global gtag */
import {
  required,
  minLength,
  maxLength,
  helpers,
} from 'vuelidate/lib/validators';
import { mapActions, mapState } from 'vuex';
import { mask } from 'vue-the-mask';
import focusTrapping from '../../mixins/focusTrapping';
import TextInput from '../shared/TextInput.vue';
import Button from '../shared/Button.vue';
import Assistance from '../shared/Assistance.vue';

const cantStartWithOneOrZero = helpers.regex('alpha', /^\([2-9][0-9][0-9]\) [0-9][0-9][0-9]-[0-9][0-9][0-9][0-9]*$/);

export default {
  data() {
    return {
      formData: {
        telephoneNumber: '',
        pic: '',
      },
      formErrors: {
        telephoneNumber:
          "Oops, you entered an invalid number. Your number must have 10 digits and can't start with a 1 or 0.",
        pic: 'Verbal security password must be entered.',
      },
      formError: false,
      loading: false,
      isTooltipOpen: {
        telephoneNumber: false,
        pic: false,
      },
    };
  },
  beforeMount() {
    this.updateFormErrorMessage('');
  },
  mounted() {
    this.$refs.title.focus();
  },
  validations: {
    formData: {
      telephoneNumber: {
        required,
        minLength: minLength(14),
        maxLength: maxLength(14),
        cantStartWithOneOrZero,
      },
      pic: {
        required,
      },
    },
  },
  computed: {
    ...mapState({
      formErrorMessage: (state) => state.formErrorMessage,
    }),
  },
  methods: {
    handleBlur(fieldName, message) {
      this.$v.formData[fieldName].$touch();
      this.setFieldErrorMessage(fieldName, message);
    },
    setFieldErrorMessage(fieldName, message) {
      if (this.$v.formData[fieldName].$invalid) {
        this.formErrors[fieldName] = message;
      } else {
        this.formErrors[fieldName] = '';
      }
    },
    handleLogin() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$anyError) {
        this.login({
          telephoneNumber: this.formData.telephoneNumber,
          pic: this.formData.pic,
        });
        this.loading = true;
        this.formError = false;
        gtag('event', 'loginClick', {
          event_label: 'log in',
          event_category: 'alternateLogin',
          value: 1,
        });
      } else {
        this.formError = true;
      }
    },
    handleInfoIconClick(name) {
      this.isTooltipOpen[name] = !this.isTooltipOpen[name];
      // Close other tooltip
      if (name === 'telephoneNumber') {
        this.isTooltipOpen.pic = false;
      } else {
        this.isTooltipOpen.telephoneNumber = false;
      }
    },
    handleDocumentClick(e) {
      // Close tooltip for clicks outside of tooltip and icon buttons
      const loginTooltip = this.$refs['telephone-number-tooltip'];
      const passwordTooltip = this.$refs['pic-tooltip'];
      const loginTooltipBtn = this.$refs['telephone-number-tooltip-btn'];
      const passwordTooltipBtn = this.$refs['pic-tooltip-btn'];

      if (loginTooltip) {
        if (
          !loginTooltip.contains(e.target)
          && !loginTooltipBtn.contains(e.target)
        ) {
          this.isTooltipOpen.telephoneNumber = false;
        }
      }
      if (passwordTooltip) {
        if (
          !passwordTooltip.contains(e.target)
          && !passwordTooltipBtn.contains(e.target)
        ) {
          this.isTooltipOpen.pic = false;
        }
      }
    },
    handleKeydown(e) {
      if (e.keyCode === 27) {
        if (this.isTooltipOpen.telephoneNumber) this.isTooltipOpen.telephoneNumber = false;
        if (this.isTooltipOpen.pic) this.isTooltipOpen.pic = false;
      }
      this.setFocus(e);
    },
    ...mapActions(['updateFormErrorMessage', 'login']),
  },
  watch: {
    formErrorMessage() {
      if (this.formErrorMessage) {
        this.loading = false;
      }
    },
  },
  mixins: [focusTrapping],
  components: {
    cbTextInput: TextInput,
    cbButton: Button,
    cbAssistance: Assistance,
  },
  directives: { mask },
};
</script>

<style lang="scss" scoped>
.cb-alternative-login {
  height: 100%;
  width: 89vw;
  max-width: 430px;
  padding-top: 40px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .cb-heading-5 {
    width: 100%;
    padding: 0 20px;
    box-sizing: border-box;
    text-align: center;
    margin-bottom: 8px;
  }

  &__global-error,
  &__error {
    padding: 0 20px;
    color: $dark-red;
    text-align: center;
  }

  &__form-container {
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    box-sizing: border-box;
    flex-grow: 1;
    margin: 12px 0 24px;
  }

  &__text-input {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    position: relative;

    button {
      border: none;
      background-color: transparent;
      padding: 0;
      margin-left: 8px;
      height: 25px;
      width: 25px;
    }

    .cb-tooltip {
      position: absolute;
      background-color: #2e384d;
      box-sizing: border-box;
      color: white;
      width: 188px;
      top: 50%;
      transform: translateY(-50%);
      right: 39px;
      padding: 20px 20px 28px 20px;
      border-radius: 10px;
      z-index: 5;

      &__arrow {
        position: absolute;
        background-color: #2e384d;
        width: 20px;
        height: 20px;
        right: -10px;
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
      }

      .assistance {
        margin: 12px 0 4px;
      }
    }
  }

  &__submit-btn {
    margin-top: 12px;
  }

  &__divider {
    height: 1px;
    width: 100%;
    background-color: $light-blue-grey;
  }

  .cb-assistance {
    padding: 16px 0;
  }

  @include desktop {
    .cb-heading-5 {
      padding: 0 40px;
      font-size: 28px;
      line-height: 38px;
    }

    &__global-error,
    &__error {
      padding: 0 40px;
    }

    &__form-container {
      padding: 0 40px;
      margin: 12px 0 28px;
    }

    &__text-input {
      margin-bottom: 20px;
      .cb-tooltip {
        right: -206px;
        top: -10px;
        transform: translateY(0);

        &__arrow {
          right: 178px;
          top: 24px;
          transform: rotate(45deg);
        }
      }
    }

    .cb-assistance {
      padding: 28px 0;
    }
  }
}
</style>
